import { useCallback, useRef } from 'react';
import { useUserSettingsPatch } from './client';
import { bestEffort } from './resilience';
import { settingsPhase } from './settingsPhase';
import type { LayerOrder, MapSettings } from './types';

/**
 * Service-only, best-effort mirror of map layer ordering. The legacy blob write
 * remains authoritative and is done elsewhere; this only forwards the final
 * ordering to `PATCH /settings/map { layer_ordering }`.
 *
 * - Phase 1: no-op.
 * - Phase >= 2: dual-write mirror, best-effort (failures are swallowed).
 *
 * Single-flight with trailing-latest: at most one request is in flight; if the
 * order changes again while one is running, only the newest order is sent when
 * the current request settles. This guarantees in-order, last-write-wins
 * delivery without request spam.
 */
export const useLayerOrderingMirror = (): ((layers: LayerOrder[]) => void) => {
  const { mutateAsync: patchMap } = useUserSettingsPatch<MapSettings>('map');
  const inFlight = useRef(false);
  const pendingLatest = useRef<LayerOrder[] | null>(null);

  const send = useCallback(
    (layers: LayerOrder[]) => {
      inFlight.current = true;
      void bestEffort(
        patchMap({ layer_ordering: { version: 1, layers } }),
        (error) =>
          console.error(
            'Best-effort layer_ordering mirror to user-settings service failed:',
            error,
          ),
      ).finally(() => {
        inFlight.current = false;
        const next = pendingLatest.current;
        if (next) {
          pendingLatest.current = null;
          send(next);
        }
      });
    },
    [patchMap],
  );

  return useCallback(
    (layers: LayerOrder[]) => {
      if (settingsPhase() < 2) return;
      if (inFlight.current) {
        pendingLatest.current = layers; // supersede any queued order
        return;
      }
      send(layers);
    },
    [send],
  );
};
